// src/components/ServiceScheduleList.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { FlatList, StyleSheet, View, Text, TouchableOpacity } from 'react-native';
import {
  ActivityIndicator,
  Button,
  Dialog,
  Portal,
  Surface,
  useTheme,
} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuth } from '../context/AuthContext';
import ServiceService from '../services/serviceService';
import {
  ServiceSchedule,
  computeDueDateStatus,
  DueDateStatus,
} from '../types/Service';
import { ErrorLogger } from '../utils/errorLogger';
import { formatShortDate as formatDate } from '../utils/formatDate';
import AddServiceScheduleBottomSheet from './AddServiceScheduleBottomSheet';
import RecordServiceBottomSheet from './RecordServiceBottomSheet';
import ServiceHistoryBottomSheet from './ServiceHistoryBottomSheet';
import FloatingActionButton from './FloatingActionButton';
import { Truck } from '../types/Truck';

interface ServiceScheduleListProps {
  truck: Truck;
}

const ServiceScheduleList: React.FC<ServiceScheduleListProps> = ({ truck }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<ServiceSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [addSheetVisible, setAddSheetVisible] = useState(false);
  const [recordSheet, setRecordSheet] = useState<ServiceSchedule | null>(null);
  const [historySheet, setHistorySheet] = useState<ServiceSchedule | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ServiceSchedule | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!user?.uid || !truck.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await ServiceService.getSchedulesByTruck(truck.id);
      setSchedules(data);
    } catch (error) {
      ErrorLogger.logError(error as any, 'ServiceScheduleList.load');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, truck.id]);

  useEffect(() => { load(); }, [load]);

  const handleToggleLifecycle = async (schedule: ServiceSchedule) => {
    if (!schedule.id) return;
    const next = schedule.lifecycleStatus === 'active' ? 'paused' : 'active';
    try {
      await ServiceService.updateLifecycleStatus(schedule.id, next);
      await load();
    } catch (error) {
      ErrorLogger.logError(error as any, 'ServiceScheduleList.handleToggleLifecycle');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete?.id) return;
    setDeleting(true);
    try {
      await ServiceService.deleteSchedule(confirmDelete.id);
      await load();
    } catch (error) {
      ErrorLogger.logError(error as any, 'ServiceScheduleList.handleDelete');
    } finally {
      setConfirmDelete(null);
      setDeleting(false);
    }
  };

  const statusStyle = (status: DueDateStatus): { bg: string; text: string } => {
    if (theme.dark) {
      if (status === 'overdue') return { bg: '#93000a', text: '#ffdad6' };
      if (status === 'due-soon') return { bg: '#5c1400', text: '#ffdbcf' };
      if (status === 'paused') return { bg: '#3a485b', text: '#c3c6d5' };
      return { bg: '#1f3352', text: '#b9c7df' };
    }
    if (status === 'overdue') return { bg: '#ffdad6', text: '#ba1a1a' };
    if (status === 'due-soon') return { bg: '#ffb59a', text: '#380d00' };
    if (status === 'paused') return { bg: '#e7eeff', text: '#434653' };
    return { bg: '#d5e3fc', text: '#57657a' };
  };

  const statusLabel = (status: DueDateStatus, schedule: ServiceSchedule): string => {
    if (status === 'overdue') return 'Overdue';
    if (status === 'paused') return 'Paused';
    const msPerDay = 1000 * 60 * 60 * 24;
    const days = Math.round((schedule.nextDueDate.getTime() - new Date().getTime()) / msPerDay);
    if (status === 'due-soon') return days === 0 ? 'Due today' : `Due in ${days}d`;
    return `Due in ${days}d`;
  };


  const renderEmptyComponent = () => (
    <View style={styles.empty}>
      <MaterialCommunityIcons name="wrench-clock" size={64} color={theme.colors.onSurfaceVariant} />
      <Text style={[styles.emptyTitle, { color: theme.colors.onSurface }]}>
        No services tracked
      </Text>
      <Text style={[styles.emptyMessage, { color: theme.colors.onSurfaceVariant }]}>
        Tap + to add a recurring service for this truck
      </Text>
    </View>
  );

  const renderItem = ({ item }: { item: ServiceSchedule }) => {
    const status = computeDueDateStatus(item);
    const chipStyle = statusStyle(status);
    return (
      <Surface style={styles.card} elevation={0}>
        <TouchableOpacity
          style={styles.cardContent}
          onPress={() => setRecordSheet(item)}
          activeOpacity={0.8}
        >
          <View style={styles.cardLeft}>
            <Text style={[styles.serviceType, { color: theme.colors.onSurface }]}>
              {item.serviceType}
            </Text>
            <Text style={[styles.dueDate, { color: theme.colors.onSurfaceVariant }]}>
              Due: {formatDate(item.nextDueDate)}
            </Text>
            <Text style={[styles.interval, { color: theme.colors.onSurfaceVariant }]}>
              Every {item.intervalDays} days
            </Text>
          </View>
          <View style={[styles.statusChip, { backgroundColor: chipStyle.bg }]}>
            <Text style={[styles.statusChipText, { color: chipStyle.text }]}>
              {statusLabel(status, item)}
            </Text>
          </View>
        </TouchableOpacity>

        <View style={styles.actionsRow}>
          <Button
            mode="text"
            compact
            icon={item.lifecycleStatus === 'active' ? 'pause' : 'play'}
            onPress={() => handleToggleLifecycle(item)}
            textColor={theme.colors.primary}
          >
            {item.lifecycleStatus === 'active' ? 'Pause' : 'Resume'}
          </Button>
          <Button
            mode="text"
            compact
            icon="history"
            onPress={() => setHistorySheet(item)}
            textColor={theme.colors.onSurfaceVariant}
          >
            History
          </Button>
          <Button
            mode="text"
            compact
            icon="delete"
            onPress={() => setConfirmDelete(item)}
            textColor={theme.colors.error}
          >
            Delete
          </Button>
        </View>
      </Surface>
    );
  };

  if (loading) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={schedules}
        renderItem={renderItem}
        keyExtractor={item => item.id || ''}
        ListEmptyComponent={renderEmptyComponent}
        contentContainerStyle={schedules.length === 0 ? styles.emptyContainer : styles.listContent}
        showsVerticalScrollIndicator={false}
      />

      <FloatingActionButton onPress={() => setAddSheetVisible(true)} />

      <AddServiceScheduleBottomSheet
        visible={addSheetVisible}
        truck={truck}
        onClose={() => setAddSheetVisible(false)}
        onScheduleCreated={() => { setAddSheetVisible(false); load(); }}
      />

      <RecordServiceBottomSheet
        visible={recordSheet !== null}
        schedule={recordSheet}
        onClose={() => setRecordSheet(null)}
        onRecorded={() => { setRecordSheet(null); load(); }}
      />

      <ServiceHistoryBottomSheet
        visible={historySheet !== null}
        schedule={historySheet}
        onClose={() => setHistorySheet(null)}
      />

      <Portal>
        <Dialog visible={confirmDelete !== null} onDismiss={() => setConfirmDelete(null)}>
          <Dialog.Icon icon="alert-circle" />
          <Dialog.Title>Delete Schedule</Dialog.Title>
          <Dialog.Content>
            <Text>
              Delete "{confirmDelete?.serviceType}" schedule? This will also remove all history
              records and cannot be undone.
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              onPress={handleDelete}
              loading={deleting}
              textColor={theme.colors.error}
            >
              Delete
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  listContent: { padding: 12, paddingBottom: 80 },
  emptyContainer: { flexGrow: 1, justifyContent: 'center' },
  card: { marginBottom: 8, borderRadius: 6, overflow: 'hidden' },
  cardContent: {
    flexDirection: 'row',
    padding: 16,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardLeft: { flex: 1, marginRight: 12 },
  serviceType: { fontFamily: 'Inter-Medium', fontSize: 16, marginBottom: 4 },
  dueDate: { fontFamily: 'Inter-Regular', fontSize: 13, marginBottom: 2 },
  interval: { fontFamily: 'Inter-Regular', fontSize: 12 },
  statusChip: { borderRadius: 9999, paddingHorizontal: 10, paddingVertical: 3 },
  statusChipText: { fontFamily: 'Inter-Medium', fontSize: 12, lineHeight: 16 },
  actionsRow: {
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingBottom: 4,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(195, 198, 213, 0.15)',
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 32,
  },
  emptyTitle: { fontFamily: 'Manrope-SemiBold', fontSize: 18, marginTop: 16, marginBottom: 8 },
  emptyMessage: { fontFamily: 'Inter-Regular', fontSize: 14, textAlign: 'center', lineHeight: 20 },
});

export default ServiceScheduleList;
